/**
 * TemplatesPaywall - Componente de paywall premium para WhatsApp Oficial e Templates
 * 
 * Exibido quando o tenant não tem acesso ao WhatsApp Oficial (exclusivo Enterprise)
 * Design premium estilo Stripe com card Enterprise dark
 */

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Crown,
  Check,
  Sparkles,
  MessageSquareText,
  Shield,
  Clock,
  FileCheck,
  BadgeCheck,
  Zap,
  BarChart3,
  Headphones,
  RotateCcw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePaywallData } from '@/hooks/usePaywallData';

interface TemplatesPaywallProps {
  currentPlan?: string;
}

// Dados das features com ícones e descrições
const officialFeatures = [
  {
    icon: BadgeCheck,
    title: 'Templates aprovados',
    description: 'Mensagens verificadas e aprovadas pelo Meta'
  },
  {
    icon: Clock,
    title: 'Sem limite de 24h',
    description: 'Envie mensagens fora da janela de conversa'
  },
  {
    icon: Zap,
    title: 'Alta confiabilidade',
    description: 'Taxa de entrega superior com API oficial'
  },
  {
    icon: FileCheck,
    title: 'Múltiplas categorias',
    description: 'Marketing, Utilitário e Autenticação'
  },
  {
    icon: BarChart3,
    title: 'Relatórios detalhados',
    description: 'Status em tempo real de cada mensagem'
  },
  {
    icon: Shield,
    title: 'Compliance Meta',
    description: 'Conformidade total com políticas WhatsApp'
  },
];

export function TemplatesPaywall({ currentPlan = 'free' }: TemplatesPaywallProps) {
  const navigate = useNavigate();
  const { enterprisePlan, isLoading, formatPrice } = usePaywallData();

  return (
    <div className="container max-w-5xl py-16">
      {/* Hero Section Premium */}
      <div className="text-center mb-16">
        {/* Ícone com gradiente animado */}
        <div className="relative inline-flex items-center justify-center w-20 h-20 mb-6">
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-500/20 via-teal-500/20 to-cyan-500/20 blur-xl animate-pulse" />
          <div className="relative flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 shadow-lg shadow-emerald-500/25">
            <MessageSquareText className="h-10 w-10 text-white" />
          </div>
        </div>
        
        <h1 className="text-4xl font-semibold tracking-tight mb-4">
          WhatsApp Business API Oficial
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-6">
          Utilize a API oficial do WhatsApp com templates aprovados pelo Meta para comunicação profissional e escalável.
        </p>
        
        {/* Alert sutil */}
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50 text-sm text-muted-foreground">
          <Crown className="h-4 w-4 text-amber-500" />
          Disponível nos planos Start, Profissional e Enterprise
        </div>
      </div>

      {/* Features Section Premium */}
      <div className="mb-16">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-semibold mb-2">Recursos que você desbloqueia</h2>
          <div className="w-24 h-1 mx-auto rounded-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {officialFeatures.map((feature, idx) => (
            <div 
              key={idx} 
              className="group p-5 rounded-xl border bg-card hover:shadow-lg hover:translate-y-[-2px] transition-all duration-200"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500/10 to-teal-500/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                  <feature.icon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-medium mb-1">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Card Enterprise - Dark Premium Centralizado */}
      <div className="max-w-lg mx-auto mb-12">
        <Card className="relative overflow-hidden bg-slate-900 text-white border-0 hover:shadow-2xl hover:shadow-amber-500/10 transition-all duration-200">
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-purple-500/5" />
          
          <div className="absolute top-4 right-4 z-10">
            <Badge className="bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 border-0 font-medium">
              <Sparkles className="h-3 w-3 mr-1" />
              Start, Profissional ou Enterprise
            </Badge>
          </div>
          
          <CardContent className="relative pt-10 pb-8 px-8 z-10">
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/25">
                <Crown className="h-7 w-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Plano Enterprise</h3>
                <p className="text-sm text-slate-400">
                  {isLoading ? (
                    <Skeleton className="h-4 w-48 bg-slate-700" />
                  ) : (
                    enterprisePlan?.description || 'Acesse WhatsApp Oficial e todos os recursos premium'
                  )}
                </p>
              </div>
            </div>
            
            {/* Preço */}
            <div className="py-8 text-center border-y border-slate-700/50 my-6">
              <p className="text-sm text-slate-400 mb-2">A partir de</p>
              {isLoading ? (
                <Skeleton className="h-14 w-40 mx-auto bg-slate-700" />
              ) : (
                <p className="text-5xl font-bold text-white">
                  R$ {formatPrice(enterprisePlan?.price_monthly)}
                  <span className="text-lg font-normal text-slate-400">/mês</span>
                </p>
              )}
            </div>
            
            {/* Diferenciais visuais */}
            <div className="grid grid-cols-3 gap-4 py-6 mb-6">
              <div className="text-center group">
                <div className="w-12 h-12 mx-auto mb-2 rounded-xl bg-slate-800 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Shield className="h-6 w-6 text-emerald-400" />
                </div>
                <p className="text-xs text-slate-400">API Oficial</p>
              </div>
              <div className="text-center group">
                <div className="w-12 h-12 mx-auto mb-2 rounded-xl bg-slate-800 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Clock className="h-6 w-6 text-amber-400" />
                </div>
                <p className="text-xs text-slate-400">Sem limite 24h</p>
              </div>
              <div className="text-center group">
                <div className="w-12 h-12 mx-auto mb-2 rounded-xl bg-slate-800 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <FileCheck className="h-6 w-6 text-purple-400" />
                </div>
                <p className="text-xs text-slate-400">Templates Meta</p>
              </div>
            </div>
            
            {/* Features do plano */}
            {enterprisePlan?.features && enterprisePlan.features.length > 0 && (
              <div className="space-y-2 mb-6">
                {enterprisePlan.features.slice(0, 4).map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-amber-400 flex-shrink-0" />
                    <span className="text-slate-300">{feature}</span>
                  </div>
                ))}
              </div>
            )}
            
            <Button 
              className="w-full h-12 bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 font-medium hover:from-amber-300 hover:to-orange-400 hover:scale-[1.02] active:scale-[0.98] transition-all"
              onClick={() => navigate('/tenant/plans')}
              disabled={isLoading}
            >
              <Crown className="mr-2 h-5 w-5" />
              Fazer Upgrade para Enterprise
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Footer de Confiança */}
      <div className="flex flex-wrap items-center justify-center gap-8 py-6 border-t">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Shield className="h-4 w-4" />
          <span>Pagamento seguro via Stripe</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Headphones className="h-4 w-4" />
          <span>Suporte dedicado</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <RotateCcw className="h-4 w-4" />
          <span>7 dias de garantia</span>
        </div>
      </div>

      <p className="text-center text-sm text-muted-foreground mt-4">
        Dúvidas? Entre em contato com nosso suporte para ajudá-lo a escolher a melhor opção.
      </p>
    </div>
  );
}
